import React, { useEffect, useMemo, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

export default function QuillEditor({ value, onChange, modules, placeholder, className, style, onImageUpload }) {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const lastValueRef = useRef('');
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const resolvedModules = useMemo(() => {
    const base = modules || {
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link', 'image'],
        ['clean']
      ]
    };

    if (!onImageUpload) return base;

    const toolbar = typeof base.toolbar === 'object' ? base.toolbar : { container: base.toolbar };
    const handlers = { ...(toolbar.handlers || {}) };
    handlers.image = async () => {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.click();
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        try {
          const url = await onImageUpload(file);
          if (!url || !quillRef.current) return;
          const editor = quillRef.current;
          const range = editor.getSelection(true);
          editor.insertEmbed(range ? range.index : 0, 'image', url);
        } catch (_) {
          // ignore
        }
      };
    };

    return {
      ...base,
      toolbar: {
        ...toolbar,
        handlers,
      }
    };
  }, [modules, onImageUpload]);

  useEffect(() => {
    if (!containerRef.current) return;
    let quill = quillRef.current;
    if (!quill) {
      quill = new Quill(containerRef.current, {
        theme: 'snow',
        modules: resolvedModules,
        placeholder: placeholder || '',
      });
      quillRef.current = quill;

      try {
        quill.root.style.minHeight = '220px';
        quill.root.style.padding = '12px';
      } catch (_) {
        // ignore
      }
    }

    const handler = () => {
      const html = quill.root.innerHTML;
      lastValueRef.current = html;
      if (typeof onChangeRef.current === 'function') onChangeRef.current(html);
    };
    quill.on('text-change', handler);

    return () => {
      quill.off('text-change', handler);
    };
  }, [resolvedModules, placeholder]);

  useEffect(() => {
    const quill = quillRef.current;
    const next = value || '';
    if (!quill) return;
    if (next === lastValueRef.current) return;
    if (quill.root.innerHTML !== next) {
      quill.clipboard.dangerouslyPasteHTML(next);
    }
    lastValueRef.current = next;
  }, [value]);

  return (
    <div className={className} style={style}>
      <div ref={containerRef} style={{ minHeight: 320 }} />
    </div>
  );
}
